// src/components/NavigationBar.jsx
import React, { useState, useEffect } from 'react';
import { 
  Tabs,
  Tab,
  Box, 
  Typography, 
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  ListItemButton,
  Divider,
  Popover
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PeopleIcon from '@mui/icons-material/People';
import ViewListIcon from '@mui/icons-material/ViewList';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VerifiedIcon from '@mui/icons-material/Verified';
import InventoryIcon from '@mui/icons-material/Inventory';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import FactoryIcon from '@mui/icons-material/Factory';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import BusinessIcon from '@mui/icons-material/Business';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BuildIcon from '@mui/icons-material/Build';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import InsightsIcon from '@mui/icons-material/Insights';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ScienceIcon from '@mui/icons-material/Science';
import { supabase } from '../supabaseClient';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import { usePermissions } from '../utils/permissions';

const NavigationBar = ({ company, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [inventoryAnchorEl, setInventoryAnchorEl] = useState(null);
  const {
    canAccessPage,
    hasPermission,
    canViewImports,
    canViewExports,
    canViewExpenses,
    canViewAccounts,
    canViewCategories,
    canViewAccountingOverview,
    canViewAccountingBanking,
    canViewAccountingReceivables,
    canViewAccountingPayables,
    canViewAccountingForecast,
    canViewInventoryPage,
    canViewInventoryTransactions,
    canViewInventoryConfigurations
  } = usePermissions();
  
  const pathParts = location.pathname.split('/');
  const basePath = pathParts[3] || 'dashboard';
  const currentPath = pathParts.length > 4 && !['equipment-list', 'equipment-maintenance', 'customer-edit', 'customer-new', 'purchase-orders'].includes(basePath) 
    ? `${basePath}-${pathParts[4]}` 
    : basePath;
  
  // Determine current category based on path
  const getCurrentCategory = () => {
    if (currentPath === 'dashboard') return 'dashboard';
    if (currentPath === 'tasks' || currentPath.startsWith('tasks-')) return 'tasks';
    if (currentPath === 'calendar-new') return 'calendar-new';
    if (currentPath === 'calendar' || currentPath.startsWith('calendar-')) return 'calendar';
    if (currentPath === 'employees' || currentPath.startsWith('employees-')) return 'employees';
    if (currentPath === 'schedule' || currentPath.startsWith('schedule-')) return 'schedule';
    if (currentPath === 'crm-overview' || currentPath === 'customers-list' || currentPath === 'customer-new' || currentPath === 'customer-edit' || currentPath === 'invoices' || currentPath === 'price-lists' || currentPath === 'proforma-invoices' || currentPath.includes('invoices') || currentPath.includes('proforma-invoices')) {
      return 'sales';
    }
    if (currentPath === 'products' || currentPath === 'brands') {
      return 'products';
    }
    if (currentPath === 'formulations' || currentPath === 'qc-tests') {
      return 'formulations';
    }
    if (currentPath === 'locations' || currentPath === 'equipment-list' || currentPath === 'equipment-maintenance') {
      return 'locations';
    }
    if (currentPath === 'line-production' || currentPath === 'line-production-templates' || currentPath === 'line-production-overview') {
      return 'line-production';
    }
    if (currentPath === 'settings') {
      return 'settings';
    }
    if (location.pathname.includes('/expenses')) {
      return 'expenses';
    }
    if (currentPath === 'accounting' || currentPath.startsWith('accounting-')) {
      return 'accounting';
    }
    if (currentPath === 'vendors' || currentPath === 'purchase-orders' || currentPath.startsWith('purchase-orders-')) {
      return 'vendors';
    }
    
    // Check session storage first before defaulting to manufacturing for manufacturing paths
    const isFromManufacturing = sessionStorage.getItem('currentNavCategory') === 'manufacturing';
    const isFromLineProduction = sessionStorage.getItem('currentNavCategory') === 'line-production';
    
    // If on manufacturing planning or requests and coming from line production, stay in line production
    if ((currentPath === 'manufacturing-planning' || currentPath === 'manufacturing-requests') && isFromLineProduction) {
      return 'line-production';
    }
    
    
    // Default manufacturing paths to manufacturing category
    if (currentPath.startsWith('manufacturing')) {
      return 'manufacturing';
    }
    
    // Check for inventory category - this should be the default if they have any inventory access
    if (currentPath.startsWith('inventory-') ||
        location.pathname.includes('/inventory/imports') ||
        location.pathname.includes('/inventory/exports') ||
        canViewImports() ||
        canViewExports()) {
      return 'inventory';
    }
    return null;
  };
  
  const currentCategory = getCurrentCategory();
  
  // Store current category in session storage for context tracking
  useEffect(() => {
    if (currentCategory) {
      sessionStorage.setItem('currentNavCategory', currentCategory);
    }
  }, [currentCategory]);
  
  // For category-filtered navigation, always show the first (and likely only) tab as active
  const currentTab = 0;

  // Get first accessible manufacturing tab for auto-redirect
  const getFirstAccessibleManufacturingTab = () => {
    const manufacturingTabs = [
      { path: `/company/${company.id}/manufacturing/overview`, permission: 'production_overview' },
      { path: `/company/${company.id}/manufacturing/batches`, permission: 'production_batches' },
      { path: `/company/${company.id}/manufacturing/planning`, permission: 'production_calendar' },
      { 
        path: `/company/${company.id}/manufacturing/requests`,
        hasAccess: () => hasPermission('production.batch_requests.manufacturing_orders.view', 'read')
      }
    ];
    
    for (const tab of manufacturingTabs) {
      if (tab.hasAccess) {
        if (tab.hasAccess()) {
          return tab.path;
        }
      } else if (!tab.permission || canAccessPage(tab.permission)) {
        return tab.path;
      }
    }
    return null;
  };

  // Get first accessible line production tab for auto-redirect
  const getFirstAccessibleLineProductionTab = () => {
    const lineProductionTabs = [
      { 
        path: `/company/${company.id}/line-production`, 
        hasAccess: () => hasPermission('production.line.page.view', 'read')
      },
      { 
        path: `/company/${company.id}/line-production/overview`, 
        hasAccess: () => hasPermission('production.line.overview.view', 'read')
      },
      { 
        path: `/company/${company.id}/line-production/templates`, 
        hasAccess: () => hasPermission('production.line.templates.view', 'read')
      },
      { 
        path: `/company/${company.id}/manufacturing/planning`, 
        permission: 'production_calendar'
      },
      { 
        path: `/company/${company.id}/manufacturing/requests`,
        hasAccess: () => hasPermission('production.batch_requests.manufacturing_orders.view', 'read')
      },
      { 
        path: `/company/${company.id}/locations`, 
        permission: 'locations'
      }
    ];
    
    for (const tab of lineProductionTabs) {
      if (tab.hasAccess) {
        if (tab.hasAccess()) {
          return tab.path;
        }
      } else if (!tab.permission || canAccessPage(tab.permission)) {
        return tab.path;
      }
    }
    return null;
  };

  // Get first accessible vendors tab for auto-redirect
  const getFirstAccessibleVendorsTab = () => {
    const vendorsTabs = [
      { 
        path: `/company/${company.id}/purchase-orders`,
        hasAccess: () => hasPermission('supply_chain.vendor_purchase_orders.view')
      },
      { 
        path: `/company/${company.id}/vendors`,
        hasAccess: () => hasPermission('supply_chain.suppliers.view')
      }
    ];
    
    for (const tab of vendorsTabs) {
      if (tab.hasAccess && tab.hasAccess()) {
        return tab.path;
      }
    }
    return null;
  };

  // Auto-redirect to first accessible manufacturing tab
  useEffect(() => {
    if (currentCategory === 'manufacturing') {
      const manufacturingPath = location.pathname.split('/').slice(4).join('/');
      const currentPermissions = {
        'overview': 'production_overview',
        'batches': 'production_batches',
        'planning': 'production_calendar',
        'requests': () => hasPermission('production.batch_requests.manufacturing_orders.view', 'read')
      };
      
      const currentPermission = currentPermissions[manufacturingPath];
      
      // If user doesn't have permission for current manufacturing page, redirect to first accessible
      if (currentPermission) {
        let hasAccess = false;
        if (typeof currentPermission === 'function') {
          hasAccess = currentPermission();
        } else {
          hasAccess = canAccessPage(currentPermission);
        }
        
        if (!hasAccess) {
          const firstAccessibleTab = getFirstAccessibleManufacturingTab();
          if (firstAccessibleTab && firstAccessibleTab !== location.pathname) {
            navigate(firstAccessibleTab, { replace: true });
          }
        }
      }
    }
    
    // Auto-redirect to first accessible line production tab
    if (currentCategory === 'line-production') {
      const lineProductionPath = location.pathname.split('/').slice(4).join('/');
      const currentPermissions = {
        '': () => hasPermission('production.line.page.view', 'read'), // main line-production page
        'overview': () => hasPermission('production.line.overview.view', 'read'),
        'templates': () => hasPermission('production.line.templates.view', 'read')
      };
      
      const currentPermission = currentPermissions[lineProductionPath];
      
      // Only redirect if user doesn't have permission for current line production page
      if (currentPermission) {
        let hasAccess = false;
        if (typeof currentPermission === 'function') {
          hasAccess = currentPermission();
        } else {
          hasAccess = canAccessPage(currentPermission);
        }
        
        if (!hasAccess) {
          const firstAccessibleTab = getFirstAccessibleLineProductionTab();
          if (firstAccessibleTab && firstAccessibleTab !== location.pathname) {
            navigate(firstAccessibleTab, { replace: true });
          }
        }
      }
    }
  }, [currentCategory, location.pathname, canAccessPage, hasPermission, navigate, company.id]);
  


  // Function to scroll to top when navigating
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
    });
  };

  // Get filtered tabs based on current category - similar to NavigationMenu filtering
  const getFilteredTabsForCategory = () => {
    if (!currentCategory) {
      // Show all tabs if no specific category (shouldn't happen but fallback)
      return [
        { 
          label: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: `/company/${company.id}/dashboard`,
          permission: 'dashboard',
          category: 'dashboard'
    },
    { 
          label: 'Tasks', 
      icon: <AssignmentIcon />, 
      path: `/company/${company.id}/tasks`,
          category: 'tasks'
    },
        { 
          label: 'Overview', 
      icon: <GroupsIcon />, 
          path: `/company/${company.id}/crm-overview`,
          category: 'sales',
          permission: 'sales_overview'
        },
        {
          label: 'Orders', 
          icon: <RequestQuoteIcon />,
          path: `/company/${company.id}/proforma-invoices`,
          category: 'sales',
          hasAccess: () => hasPermission('sales.purchase_orders.page.view', 'read')
        },
        { 
          label: 'Invoices', 
          icon: <ReceiptIcon />, 
          path: `/company/${company.id}/invoices`,
          category: 'sales',
          permission: 'invoices'
        },
        {
          label: 'Price Lists', 
          icon: <LocalOfferIcon />,
          path: `/company/${company.id}/price-lists`,
          category: 'sales',
          permission: 'price_lists'
        },
        {
          label: 'Customers', 
          icon: <PeopleIcon />,
          path: `/company/${company.id}/customers-list`,
          category: 'sales',
          permission: 'customers'
        },
        {
          label: 'Brands',
          icon: <BusinessCenterIcon />,
          path: `/company/${company.id}/brands`,
          category: 'products',
          permission: 'brands'
        },
        {
          label: 'Products',
          icon: <LocalMallIcon />,
          path: `/company/${company.id}/products`,
          category: 'products',
          permission: 'products'
        },

        { 
          label: 'Locations',
          icon: <LocationCityIcon />, 
          path: `/company/${company.id}/locations`,
          category: 'inventory',
          permission: 'locations'
        },

        {
          label: 'Overview',
          icon: <AssessmentIcon />,
          path: `/company/${company.id}/manufacturing/overview`,
          pathKey: 'manufacturing-overview',
          category: 'manufacturing',
          permission: 'production_overview'
        },
        {
          label: 'Batches',
          icon: <LocalDrinkIcon />,
          path: `/company/${company.id}/manufacturing/batches`,
          pathKey: 'manufacturing-batches',
          category: 'manufacturing',
          permission: 'production_batches'
        },
        {
          label: 'Production Planning',
          icon: <CalendarMonthIcon />,
          path: `/company/${company.id}/manufacturing/planning`,
          pathKey: 'manufacturing-planning',
          category: 'manufacturing',
          permission: 'production_calendar'
        },
        {
          label: 'Requests',
          icon: <AssignmentIcon />,
          path: `/company/${company.id}/manufacturing/requests`,
          pathKey: 'manufacturing-requests',
          category: 'manufacturing',
          hasAccess: () => hasPermission('production.batch_requests.manufacturing_orders.view', 'read')
        },
        {
          label: 'Locations',
          icon: <LocationCityIcon />,
          path: `/company/${company.id}/locations`,
          category: 'manufacturing',
          permission: 'locations'
        },
        { 
          label: 'Settings', 
          icon: <SettingsIcon />, 
          path: `/company/${company.id}/settings`,
          category: 'settings'
        }
      ];
    }

    // Filter tabs based on current category - only show relevant ones
    const allTabs = [
      { 
        label: 'Dashboard', 
      icon: <DashboardIcon />, 
      path: `/company/${company.id}/dashboard`,
        permission: 'dashboard',
        category: 'dashboard'
    },
    { 
        label: 'Tasks', 
      icon: <AssignmentIcon />, 
      path: `/company/${company.id}/tasks`,
        category: 'tasks'
    },
          { 
        label: 'Overview', 
        icon: <GroupsIcon />, 
        path: `/company/${company.id}/crm-overview`,
        pathKey: 'crm-overview',
        category: 'sales',
        permission: 'sales_overview'
      },
      { 
        label: 'Orders', 
        icon: <RequestQuoteIcon />, 
        path: `/company/${company.id}/proforma-invoices`,
        pathKey: 'proforma-invoices',
        category: 'sales',
        hasAccess: () => hasPermission('sales.purchase_orders.page.view', 'read')
      },
      { 
        label: 'Invoices', 
        icon: <ReceiptIcon />, 
        path: `/company/${company.id}/invoices`,
        category: 'sales',
        permission: 'invoices'
      },
      { 
        label: 'Price Lists', 
        icon: <LocalOfferIcon />, 
        path: `/company/${company.id}/price-lists`,
        category: 'sales',
        permission: 'price_lists'
      },
      { 
        label: 'Customers', 
        icon: <PeopleIcon />, 
        path: `/company/${company.id}/customers-list`,
        pathKey: 'customers-list',
        category: 'sales',
        permission: 'customers'
      },
      {
        label: 'Overview',
        icon: <InsightsIcon />,
        path: `/company/${company.id}/accounting/overview`,
        pathKey: 'accounting-overview',
        category: 'accounting',
        hasAccess: () => canViewAccountingOverview()
      },
      {
        label: 'Banking',
        icon: <AccountBalanceWalletIcon />,
        path: `/company/${company.id}/accounting/banking`,
        pathKey: 'accounting-banking',
        category: 'accounting',
        hasAccess: () => canViewAccountingBanking()
      },
      {
        label: 'Receivables',
        icon: <ReceiptLongIcon />,
        path: `/company/${company.id}/accounting/receivables`,
        pathKey: 'accounting-receivables',
        category: 'accounting',
        hasAccess: () => canViewAccountingReceivables()
      },
      {
        label: 'Payables',
        icon: <CreditScoreIcon />,
        path: `/company/${company.id}/accounting/payables`,
        pathKey: 'accounting-payables',
        category: 'accounting',
        hasAccess: () => canViewAccountingPayables()
      },
      {
        label: 'Forecast',
        icon: <ShowChartIcon />,
        path: `/company/${company.id}/accounting/forecast`,
        pathKey: 'accounting-forecast',
        category: 'accounting',
        hasAccess: () => canViewAccountingForecast()
      },
      {
        label: 'Expenses',
        icon: <ReceiptIcon />,
        path: `/company/${company.id}/expenses`,
        category: 'expenses',
        hasAccess: () => canViewExpenses()
      },
      {
        label: 'Accounts',
        icon: <AccountBalanceIcon />,
        path: `/company/${company.id}/expenses/accounts`,
        category: 'expenses',
        hasAccess: () => canViewAccounts()
      },
      {
        label: 'Categories',
        icon: <ViewListIcon />,
        path: `/company/${company.id}/expenses/categories`,
        category: 'expenses',
        hasAccess: () => canViewCategories()
      },
      {
        label: 'Products',
        icon: <LocalMallIcon />,
        path: `/company/${company.id}/products`,
        category: 'products',
        permission: 'products'
      },

      {
        label: 'Brands',
        icon: <BusinessCenterIcon />,
        path: `/company/${company.id}/brands`,
        category: 'products',
        permission: 'brands'
      },
      {
        label: 'QC Tests',
        icon: <VerifiedIcon />,
        path: `/company/${company.id}/qc-tests`,
        category: 'formulations',
        hasAccess: () => {
          return hasPermission('inventory.supplies.qc_tests.view') ||
                 hasPermission('inventory.raw_materials.qc_tests.view');
        }
      },

      {
        label: 'Overview',
        icon: <AssessmentIcon />,
        path: `/company/${company.id}/manufacturing/overview`,
        pathKey: 'manufacturing-overview',
        category: 'manufacturing',
        permission: 'production_overview'
      },
      {
        label: 'Batches',
        icon: <LocalDrinkIcon />,
        path: `/company/${company.id}/manufacturing/batches`,
        pathKey: 'manufacturing-batches',
        category: 'manufacturing',
        permission: 'production_batches'
      },
      {
        label: 'Production Planning',
        icon: <CalendarMonthIcon />,
        path: `/company/${company.id}/manufacturing/planning`,
        pathKey: 'manufacturing-planning',
        category: 'manufacturing',
        permission: 'production_calendar'
      },
      {
          label: 'Requests',
          icon: <AssignmentIcon />,
          path: `/company/${company.id}/manufacturing/requests`,
          pathKey: 'manufacturing-requests',
          category: 'manufacturing',
          hasAccess: () => hasPermission('production.batch_requests.manufacturing_orders.view', 'read')
        },
    { 
        label: 'Settings', 
      icon: <SettingsIcon />, 
      path: `/company/${company.id}/settings`,
        category: 'settings'
      }
    ];

    // Filter based on current category
    if (currentCategory === 'sales') {
      return allTabs.filter(tab => tab.category === 'sales');
    } else if (currentCategory === 'expenses') {
      return allTabs.filter(tab => tab.category === 'expenses');
    } else if (currentCategory === 'accounting') {
      return allTabs.filter(tab => tab.category === 'accounting');
    } else if (currentCategory === 'products') {
      return allTabs.filter(tab => tab.category === 'products');
    } else if (currentCategory === 'formulations') {
      const formulationsTabs = [];
      if (canAccessPage('formulations')) {
        formulationsTabs.push({
          label: 'Formulations',
          icon: <ScienceIcon />,
          path: `/company/${company.id}/formulations`,
          category: 'formulations'
        });
      }
      const hasQCAccess = hasPermission('inventory.supplies.qc_tests.view') ||
                         hasPermission('inventory.raw_materials.qc_tests.view');
      if (hasQCAccess) {
        formulationsTabs.push({
          label: 'QC Tests',
          icon: <VerifiedIcon />,
          path: `/company/${company.id}/qc-tests`,
          category: 'formulations'
        });
      }
      return formulationsTabs;
    } else if (currentCategory === 'inventory') {
      const inventoryTabs = [];
      
      // Check if user has access to any inventory sub-tabs (for dropdown)
      const hasInventorySubTabAccess = canViewInventoryPage() ||
                                      canViewInventoryTransactions() ||
                                      canViewInventoryConfigurations();
      
      // 1. Inventory tab (if has access)
      if (hasInventorySubTabAccess) {
        inventoryTabs.push({
          label: 'Inventory',
          icon: <InventoryIcon />,
          category: 'inventory',
          path: (() => {
            if (canViewInventoryPage()) return `/company/${company.id}/inventory/inventory/inventory-list`;
            if (canViewInventoryTransactions()) return `/company/${company.id}/inventory/inventory/inventory-transactions`;
            return `/company/${company.id}/inventory/inventory/inventory-configurations`;
          })()
        });
      }
      
      // 2. Imports tab (if has view permission)
      if (canViewImports()) {
        inventoryTabs.push({
          label: 'Imports',
          icon: <InputIcon />,
          path: `/company/${company.id}/inventory/imports`,
          category: 'inventory'
        });
      }
      
      // 3. Exports tab (if has view permission)
      if (canViewExports()) {
        inventoryTabs.push({
          label: 'Exports',
          icon: <OutputIcon sx={{ transform: 'scaleX(-1)' }} />,
          path: `/company/${company.id}/inventory/exports`,
          category: 'inventory'
        });
      }
      
      return inventoryTabs;
    } else if (currentCategory === 'manufacturing') {
      return allTabs.filter(tab => tab.category === 'manufacturing' && (!tab.permission || canAccessPage(tab.permission)));
    } else if (currentCategory === 'line-production') {
      return [
        {
          label: 'Overview',
          icon: <AssessmentIcon />,
          path: `/company/${company.id}/line-production/overview`,
          pathKey: 'line-production-overview',
          category: 'line-production',
          hasAccess: () => hasPermission('production.line.overview.view', 'read')
        },
        {
          label: 'Production Lines',
          icon: <FactoryIcon />,
          path: `/company/${company.id}/line-production`,
          pathKey: 'line-production',
          category: 'line-production',
          hasAccess: () => hasPermission('production.line.page.view', 'read')
        },
        {
          label: 'Production Planning',
          icon: <CalendarMonthIcon />,
          path: `/company/${company.id}/manufacturing/planning`,
          pathKey: 'manufacturing-planning',
          category: 'line-production',
          permission: 'production_calendar'
        },
        {
          label: 'Requests',
          icon: <AssignmentIcon />,
          path: `/company/${company.id}/manufacturing/requests`,
          pathKey: 'manufacturing-requests',
          category: 'line-production',
          hasAccess: () => hasPermission('production.batch_requests.manufacturing_orders.view', 'read')
        },
        {
          label: 'Product Templates',
          icon: <PrecisionManufacturingIcon />,
          path: `/company/${company.id}/line-production/templates`,
          pathKey: 'line-production-templates',
          category: 'line-production',
          hasAccess: () => hasPermission('production.line.templates.view', 'read')
        }
      ];
    } else if (currentCategory === 'settings') {
      return allTabs.filter(tab => tab.category === 'settings');
    } else if (currentCategory === 'dashboard') {
      return allTabs.filter(tab => tab.category === 'dashboard');
    } else if (currentCategory === 'tasks') {
      return allTabs.filter(tab => tab.category === 'tasks');
    } else if (currentCategory === 'calendar') {
      return allTabs.filter(tab => tab.category === 'calendar');
    } else if (currentCategory === 'calendar-new') {
      return []; // No tabs for calendar-new, it has its own dedicated page
    } else if (currentCategory === 'employees') {
      return []; // No tabs for employees, it has its own dedicated page
    } else if (currentCategory === 'schedule') {
      return [
        { 
          label: 'Schedule', 
          icon: <ScheduleIcon />, 
          path: `/company/${company.id}/schedule`,
          category: 'schedule',
          permission: 'schedule'
        },
        { 
          label: 'Employee Tasks', 
          icon: <AssignmentIcon />, 
          path: `/company/${company.id}/schedule/tasks`,
          pathKey: 'schedule-tasks',
          category: 'schedule',
          permission: 'schedule'
        },
        { 
          label: 'Analytics', 
          icon: <AssessmentIcon />, 
          path: `/company/${company.id}/schedule/analytics`,
          pathKey: 'schedule-analytics',
          category: 'schedule',
          permission: 'schedule'
        }
      ];
    } else if (currentCategory === 'locations') {
      return [
        { 
          label: 'Locations', 
          icon: <LocationCityIcon />, 
          path: `/company/${company.id}/locations`,
          category: 'locations',
          hasAccess: () => hasPermission('locations.locations.view')
        },
        { 
          label: 'Equipment List', 
          icon: <EngineeringIcon />, 
          path: `/company/${company.id}/equipment-list`,
          pathKey: 'equipment-list',
          category: 'locations',
          hasAccess: () => hasPermission('locations.equipment_list.view')
        },
        { 
          label: 'Maintenance', 
          icon: <BuildIcon />, 
          path: `/company/${company.id}/equipment-maintenance`,
          pathKey: 'equipment-maintenance',
          category: 'locations',
          hasAccess: () => hasPermission('locations.equipment_maintenance.view')
        }
      ];
    } else if (currentCategory === 'vendors') {
      return [
        { 
          label: 'Purchase Orders', 
          icon: <AssignmentIcon />, 
          path: `/company/${company.id}/purchase-orders`,
          pathKey: 'purchase-orders',
          category: 'vendors',
          hasAccess: () => hasPermission('supply_chain.vendor_purchase_orders.view')
        },
        { 
          label: 'Vendors', 
          icon: <AccountBoxIcon />, 
          path: `/company/${company.id}/vendors`,
          pathKey: 'vendors',
          category: 'vendors',
          hasAccess: () => hasPermission('supply_chain.suppliers.view')
        }
      ];
    }

    return allTabs;
  };

  const categoryFilteredTabs = getFilteredTabsForCategory();

  // Filter tabs based on permissions
  const filteredTabs = categoryFilteredTabs.filter(tab => {
    if (tab.hasAccess) {
      return tab.hasAccess();
    }
    if (tab.permission) {
      return canAccessPage(tab.permission);
    }
    return true;
  });

  // Helper functions to filter menu items based on permissions
  const filterMenuItems = (items) => {
    return items.filter(item => {
      if (item.hasAccess) {
        return item.hasAccess();
      }
      if (item.permission) {
        return canAccessPage(item.permission);
      }
      return true;
    });
  };

  const handleTabClick = (tab, index) => {
    if (tab.path) {
      navigate(tab.path);
      scrollToTop();
    }
  };

  const handleInventoryClick = (event) => {
    setInventoryAnchorEl(event.currentTarget);
  };

  const handleInventoryClose = () => {
    setInventoryAnchorEl(null);
  };

  const handleInventoryItemClick = (path) => {
    navigate(path);
    scrollToTop();
    handleInventoryClose();
  };

  const inventoryItems = [
    {
      label: 'Master Inventory',
      icon: <InventoryIcon />,
      path: (() => {
        if (canViewInventoryPage()) return `/company/${company.id}/inventory/inventory/inventory-list`;
        if (canViewInventoryTransactions()) return `/company/${company.id}/inventory/inventory/inventory-transactions`;
        return `/company/${company.id}/inventory/inventory/inventory-configurations`;
      })(),
      hasAccess: () => canViewInventoryPage() || canViewInventoryTransactions() || canViewInventoryConfigurations()
    }
  ].filter(item => item.hasAccess());

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Top Navigation Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 32px',
          background: '#ffffff',
          borderBottom: '1px solid rgba(0, 0, 0, 0.18)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100vw',
          maxWidth: '100%',
          zIndex: 1100,
          boxShadow: 'none'
        }}
      >
        {/* Logo */}
          <Typography
            variant="h6"
            onClick={() => navigate(`/company/${company.id}/menu`)}
            sx={{
              fontWeight: 500,
              lineHeight: 1.2,
              color: '#1e1e1e',
              fontSize: '1.5rem',
              letterSpacing: '1px',
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'scale(1.02)'
              }
            }}
          >
            GlowWorks
          </Typography>

                {/* Navigation Tabs - Show tabs except for single dashboard/tasks/calendar/calendar-new/employees/schedule/settings */}
        {filteredTabs.length > 0 && !(filteredTabs.length <= 1 && (currentCategory === 'dashboard' || currentCategory === 'tasks' || currentCategory === 'calendar' || currentCategory === 'calendar-new' || currentCategory === 'employees' || currentCategory === 'schedule' || currentCategory === 'settings' || currentCategory === 'formulations')) && (
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center', gap: 1, position: 'relative' }}>
            {filteredTabs.map((tab, index) => (
              <Button
                key={tab.label}
                disableRipple
                disableFocusRipple
                disableTouchRipple
                onClick={(e) => tab.isDropdown ? handleInventoryClick(e) : handleTabClick(tab, index)}
                sx={{
                  color: '#1e1e1e',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  letterSpacing: '0.5px',
                  textTransform: 'none',
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: 'transparent',
                  position: 'relative',
                  zIndex: 2,
                  '&:hover': {
                    background: 'rgba(0, 0, 0, 0.05) !important',
                    color: '#1e1e1e !important'
                  },
                  '&:active': {
                    background: 'rgba(0, 0, 0, 0.08) !important',
                    color: '#1e1e1e !important'
                  },
                  '&:focus': {
                    background: 'transparent !important',
                    color: '#1e1e1e !important'
                  },
                  '&.Mui-focusVisible': {
                    background: 'transparent !important',
                    color: '#1e1e1e !important'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {tab.icon}
                  {tab.label}
                  {tab.isDropdown && <KeyboardArrowDownIcon sx={{ ml: 0.5, fontSize: '1rem' }} />}
                </Box>
              </Button>
            ))}
            
            {/* Sliding underline */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: '50%',
                height: '2px',
                background: '#1e1e1e',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: `translateX(${(() => {
                  const activeIndex = filteredTabs.findIndex(tab => 
                    currentPath === (tab.pathKey || tab.label.toLowerCase().replace(' ', '-')) || 
                    (tab.path && location.pathname === tab.path) ||
                    (tab.label === 'Customers' && (location.pathname.includes('/customer-new') || location.pathname.includes('/customer-edit'))) ||
                    (tab.label === 'Orders' && location.pathname.includes('/proforma-invoices')) ||
                    (tab.label === 'Invoices' && location.pathname.includes('/invoices')) ||
                    (tab.label === 'Purchase Orders' && (location.pathname.includes('/purchase-orders') || currentPath === 'purchase-orders')) ||
                    (tab.label === 'Vendors' && (location.pathname.includes('/vendors') && !location.pathname.includes('/purchase-orders'))) ||
                    (tab.label === 'Imports' && location.pathname.includes('/inventory/imports')) ||
                    (tab.label === 'Exports' && location.pathname.includes('/inventory/exports')) ||
                    (tab.label === 'Inventory' && location.pathname.includes('/inventory/inventory/')) ||
                    (tab.isDropdown && currentPath.includes('inventory') && !location.pathname.includes('/inventory/imports') && !location.pathname.includes('/inventory/exports')) ||
                    (currentPath.includes('templates') && tab.pathKey === 'line-production-templates') ||
                    (currentPath.includes('overview') && tab.pathKey === 'line-production-overview')
                  );
                  if (activeIndex === -1) return '-50%';
                  
                  // Calculate total width of all tabs
                  let totalWidth = 0;
                  filteredTabs.forEach(tab => {
                    const tabLabel = tab.label;
                    const hasIcon = !!tab.icon;
                    const hasDropdown = tab.isDropdown;
                    const tabWidth = tabLabel.length * 9 + (hasIcon ? 24 : 0) + (hasDropdown ? 20 : 0) + 48;
                    totalWidth += tabWidth + 4; // 4px gap
                  });
                  totalWidth -= 4; // Remove last gap
                  
                  // Calculate offset from center to active tab
                  let offsetFromStart = 0;
                  for (let i = 0; i < activeIndex; i++) {
                    const tabLabel = filteredTabs[i].label;
                    const hasIcon = !!filteredTabs[i].icon;
                    const hasDropdown = filteredTabs[i].isDropdown;
                    const tabWidth = tabLabel.length * 9 + (hasIcon ? 24 : 0) + (hasDropdown ? 20 : 0) + 48;
                    offsetFromStart += tabWidth + 4; // 4px gap
                  }
                  
                  // Calculate active tab width for centering
                  const activeTab = filteredTabs[activeIndex];
                  const hasIcon = !!activeTab.icon;
                  const hasDropdown = activeTab.isDropdown;
                  const activeTabWidth = activeTab.label.length * 9 + (hasIcon ? 24 : 0) + (hasDropdown ? 20 : 0) + 48;
                  
                  // Position relative to center
                  const centerOffset = -totalWidth / 2;
                  const activeTabCenter = offsetFromStart + activeTabWidth / 2;
                  const finalOffset = centerOffset + activeTabCenter - activeTabWidth / 2;
                  
                  return `${finalOffset}px`;
                })()})`,
                width: (() => {
                  const activeIndex = filteredTabs.findIndex(tab => 
                    currentPath === (tab.pathKey || tab.label.toLowerCase().replace(' ', '-')) || 
                    (tab.path && location.pathname === tab.path) ||
                    (tab.label === 'Customers' && (location.pathname.includes('/customer-new') || location.pathname.includes('/customer-edit'))) ||
                    (tab.label === 'Orders' && location.pathname.includes('/proforma-invoices')) ||
                    (tab.label === 'Invoices' && location.pathname.includes('/invoices')) ||
                    (tab.label === 'Imports' && location.pathname.includes('/inventory/imports')) ||
                    (tab.label === 'Exports' && location.pathname.includes('/inventory/exports')) ||
                    (tab.label === 'Inventory' && location.pathname.includes('/inventory/inventory/')) ||
                    (tab.isDropdown && currentPath.includes('inventory') && !location.pathname.includes('/inventory/imports') && !location.pathname.includes('/inventory/exports')) ||
                    (currentPath.includes('templates') && tab.pathKey === 'line-production-templates') ||
                    (currentPath.includes('overview') && tab.pathKey === 'line-production-overview')
                  );
                  if (activeIndex === -1) return '0px';
                  
                  const activeTab = filteredTabs[activeIndex];
                  const hasIcon = !!activeTab.icon;
                  const hasDropdown = activeTab.isDropdown;
                  const tabWidth = activeTab.label.length * 9 + (hasIcon ? 24 : 0) + (hasDropdown ? 20 : 0) + 48; // text + icon + dropdown + padding
                  
                  return `${tabWidth}px`;
                })(),
                opacity: (() => {
                  const activeIndex = filteredTabs.findIndex(tab => 
                    currentPath === (tab.pathKey || tab.label.toLowerCase().replace(' ', '-')) || 
                    (tab.path && location.pathname === tab.path) ||
                    (tab.label === 'Customers' && (location.pathname.includes('/customer-new') || location.pathname.includes('/customer-edit'))) ||
                    (tab.label === 'Orders' && location.pathname.includes('/proforma-invoices')) ||
                    (tab.label === 'Invoices' && location.pathname.includes('/invoices')) ||
                    (tab.label === 'Imports' && location.pathname.includes('/inventory/imports')) ||
                    (tab.label === 'Exports' && location.pathname.includes('/inventory/exports')) ||
                    (tab.label === 'Inventory' && location.pathname.includes('/inventory/inventory/')) ||
                    (tab.isDropdown && currentPath.includes('inventory') && !location.pathname.includes('/inventory/imports') && !location.pathname.includes('/inventory/exports')) ||
                    (currentPath.includes('templates') && tab.pathKey === 'line-production-templates') ||
                    (currentPath.includes('overview') && tab.pathKey === 'line-production-overview')
                  );
                  return activeIndex !== -1 ? 1 : 0;
                })()
              }}
            />
        </Box>
        )}

        {/* Right Side Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box 
            onClick={() => navigate(`/company/${company.id}/menu`)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover .company-arrow': {
                opacity: 1,
                transform: 'translateX(0px)'
              }
            }}
          >
            <ArrowBackIcon 
              className="company-arrow"
              sx={{
                color: '#1e1e1e',
                fontSize: '1.2rem',
                opacity: 0,
                transform: 'translateX(-10px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                lineHeight: 1.2,
                color: '#1e1e1e',
                fontSize: '1.5rem',
                letterSpacing: '1px',
                cursor: 'pointer'
              }}
            >
              {company.name}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Inventory Dropdown */}
      <Popover
        open={Boolean(inventoryAnchorEl)}
        anchorEl={inventoryAnchorEl}
        onClose={handleInventoryClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            background: '#000000',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            mt: 1,
            minWidth: '200px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)'
          }
        }}
      >
        <Box sx={{ py: 1 }}>
          {inventoryItems.map((item, index) => (
            <MenuItem
              key={item.label}
              onClick={() => handleInventoryItemClick(item.path)}
              sx={{
                color: '#1e1e1e !important',
                py: 1.5,
                px: 2,
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.08)',
                  color: '#1e1e1e !important'
                },
                '& *': {
                  color: '#1e1e1e !important'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ListItemIcon sx={{ color: '#1e1e1e !important', minWidth: '36px' }}>
                {item.icon}
              </ListItemIcon>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: '#1e1e1e !important'
                }}
                style={{ color: '#ffffff' }}
              >
                {item.label}
              </Typography>
            </MenuItem>
          ))}
        </Box>
      </Popover>

      
      </Box>
  );
};

export default NavigationBar;